import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import type { Editor } from '@tiptap/react';
import { BarChart2, FileText as FileTextIcon } from 'lucide-react';
import {
  Avatar,
  Body,
  Button,
  Caption,
  Card,
  Heading,
  I,
  Icon,
  Label,
  Overline,
  Pill,
  Row,
  Stack,
  Tooltip,
} from '@leanin/ui';
import { DropZone } from '@leanin/ui/client';
import { useNavigate } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import type { components } from '@leanin/api-client';
import {
  isAttachableInputKind,
  type AttachedEntityView,
  type MentionView,
  type PostMediaView,
} from '@leanin/shared';
import { mediaUrlFromKey } from '@leanin/shared/media-url';
import {
  isPostMediaContentType,
  POST_MEDIA_CONTENT_TYPES,
  type PostMediaContentType,
} from '@leanin/shared/post-media';
import { validatePollOptions } from '@leanin/shared/polls';

import {
  useCreateFeedPost,
  useFinalizeFeedPostMedia,
  usePresignFeedPostMedia,
  useResolveFeedAttachment,
  useResolveFeedUrl,
  useResolveLinkPreview,
  useUpdateFeedPost,
  type FinalizePostMediaInput,
  type FinalizePostMediaResult,
  type LinkPreviewInput,
  type UpdateFeedPostMediaItem,
} from '../../lib/mutations/feed';
import { ImageCropDialog } from '../media/ImageCropDialog';
import { fetchImageAsFile } from '../media/fetch-image-file';
import { PostEntityCard } from './PostEntityCard';
import { PollComposerBlock } from './PollComposerBlock';
import { newPollDraft, type PollDraft } from './poll-draft';
import { parseStoredComposerDraft, type StoredComposerDraft } from './composer-draft';
import { usePollComposerEnabled } from './use-post-capabilities';
import { LinkPreviewCard } from '@/components/link-preview';
import { PostMediaGallery, type EditableMediaItem } from './PostMediaGallery';
import { PostMediaUploadButton } from './PostMediaUploadButton';
import { uploadWithProgress, UploadSemaphore } from '../../lib/upload';
import { groupKindToType, type GroupType } from '../../lib/analytics/events';
import { EDITOR_BODY_FORMAT, normalizeLegacyBody } from '../editor';
import { RichTextEditor, type RichTextEditorHandle } from '../editor/RichTextEditor';
import { RichTextToolbar } from '../editor/RichTextToolbar';
import { feedErrorMessage } from './feed-error-copy';

export type PostMediaInput = components['schemas']['PostMediaInput'];

export type PostComposerScope = {
  kind: 'circle' | 'network' | 'space';
  id: string;
  // Optional display name surfaced in the "Posting to …" pill. Without it the
  // composer falls back to "1 space" when the id isn't found in `myGroups`.
  name?: string;
};

export type ComposerGroup = {
  id: string;
  name: string;
  kind: 'circle' | 'network' | 'space';
};

export type PostComposerHandle = {
  /** `focus()` + inserts the given text at the cursor once the editor is
   *  mounted — the "Post it" path for suggested discussion prompts. */
  prefill: (text: string) => void;
  /** Scrolls the composer into view and focuses the body textarea, expanding
   *  the collapsed prompt first if necessary. */
  focus: () => void;
};

type Selection =
  | { readonly kind: 'community' }
  | { readonly kind: 'spaces'; readonly ids: ReadonlySet<string> }
  | { readonly kind: 'none' };

/** Destination of a created post in member-facing vocabulary ('space' →
 *  'group'); 'multi' when several spaces were selected. */
export type PostAudience = GroupType | 'community' | 'multi';

const AUDIENCE_HINT_SEEN_KEY = 'leanin.composer.audienceHintSeen';

type Props = {
  /** Viewer projection. `id` keys per-member draft persistence in localStorage. */
  viewer: { id: string; name: string; photoUrl?: string };
  onClose?: () => void;
  scope?: PostComposerScope;
  myGroups?: ReadonlyArray<ComposerGroup>;
  composerHandle?: Ref<PostComposerHandle>;
  /** Expand + focus the editor as soon as the sheet mounts. Deep links
   *  (`?compose=1`) mount this sheet only after the page's async data
   *  lands, so a parent's one-frame handle deferral can fire before the
   *  sheet exists — the sheet owning the behavior makes it reliable. */
  autoFocusOnMount?: boolean;
  /** Pre-seed the attachment list on first mount when no persisted draft exists.
   *  Used by share-entry-point modals to open the composer with the target
   *  entity already attached. */
  initialAttached?: ReadonlyArray<AttachedEntityView>;
  /** Override the draft-storage key so isolated callers (e.g. per-entity share
   *  modals) don't pollute the per-viewer/per-scope draft used by the inline
   *  composer. */
  draftKeyOverride?: string;
  /** 'card' (default) wraps the form in a Card surface and renders the inline
   *  close button. 'flush' renders the form bare for hosting inside a Dialog
   *  whose own surface + close button replace the chrome. */
  chrome?: 'card' | 'flush';
  /** Extra controls injected into the left toolbar Row, after the built-in
   *  formatting and upload buttons. Used by share modals to add a
   *  Copy-link affordance alongside the composer tools. */
  toolbarExtras?: ReactNode;
  /** 'create' (default) drafts a new post. 'edit' rebinds the composer to an
   *  existing post: body, formatting, and media are editable; attachments and
   *  audience stay fixed, and there is no draft persistence. The create path
   *  is unchanged. */
  mode?: 'create' | 'edit';
  /** Id of the post being edited. Required when `mode === 'edit'`. */
  editPostId?: string;
  /** Seeds the body. Edit mode: the post's current body. Create mode: a
   *  first-mount seed (e.g. a share modal's pre-quoted text) that a
   *  persisted draft for the draft key overwrites. */
  initialBody?: string;
  /** Dialect of `initialBody`. Legacy `'v1'` bodies are normalized into the
   *  current dialect before hydrating the editor; saving re-writes them as
   *  `'v2'`. */
  initialBodyFormat?: 'v1' | 'v2';
  /** Resolved mentions of the post being edited — lets the editor render
   *  `@handle` tokens in `initialBody` as display-name chips. */
  initialMentions?: ReadonlyArray<MentionView>;
  /** Seeds the media list when `mode === 'edit'`. */
  initialMedia?: ReadonlyArray<PostMediaView>;
  /** Called with the updated body + mentions + media after a successful
   *  edit save. */
  onSaved?: (updated: {
    body: string;
    bodyFormat: 'v1' | 'v2';
    mentions: ReadonlyArray<MentionView>;
    media: ReadonlyArray<PostMediaView>;
  }) => void;
  /** Create-mode counterpart of `onSaved` — called with the new post id,
   *  the resolved audience, and the created post view (so the feed can show
   *  the new post at the top immediately) after a successful create. */
  onPosted?: (created: {
    postId: string;
    audience: PostAudience;
    post: components['schemas']['PostView'];
  }) => void;
};

const MEDIA_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const DOCUMENT_UPLOAD_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ACCEPTED_UPLOAD_TYPES = [...MEDIA_UPLOAD_TYPES, ...DOCUMENT_UPLOAD_TYPES];
const MAX_UPLOAD_BYTES = 12 * 1024 * 1024; // 12 MB
// Raster images the crop dialog can round-trip through a canvas. GIF is
// deliberately excluded — canvas re-encode would flatten the animation.
const CROPPABLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_INFLIGHT_UPLOADS = 3;
const MAX_ATTACHMENTS = 3;
const MAX_LINK_PREVIEWS = 3;
const DRAFT_VERSION = 5;

type StagedMedia = EditableMediaItem & {
  /** Server storage key after presign — set immediately at presign time. */
  storageKey: string | null;
  /** Original file (kept for retry; cleared after successful PUT). */
  file: File | null;
  /** Size in bytes (for the eventual PostMediaInput). */
  sizeBytes: number;
  /** Original mime; PostMediaView's contentType. */
  mimeType: PostMediaContentType;
  /** post_media row id when the tile is an already-published item being
   *  edited; null for uploads staged in this session. */
  existingId: string | null;
};

function audienceLabel(
  selection: Selection,
  myGroups: ReadonlyArray<ComposerGroup>,
  scope: PostComposerScope | undefined,
  t: ReturnType<typeof useTranslations<'Feed'>>,
): string | null {
  if (selection.kind === 'community') return t('composer.audienceCommunity');
  if (selection.kind === 'none') return null;
  if (selection.ids.size === 1) {
    const id = Array.from(selection.ids)[0]!;
    if (scope !== undefined && scope.id === id && scope.name !== undefined) {
      return scope.name;
    }
    return myGroups.find((g) => g.id === id)?.name ?? t('composer.audienceSpaces', { count: 1 });
  }
  return t('composer.audienceSpaces', { count: selection.ids.size });
}

function audienceIcon(
  selection: Selection,
  myGroups: ReadonlyArray<ComposerGroup>,
  scope: PostComposerScope | undefined,
): React.ReactNode {
  if (selection.kind === 'community') return I.globe;
  // `none` is unreachable here — the pill only renders when audienceLabel
  // returns a non-null string, which excludes `none`.
  if (selection.kind === 'spaces' && selection.ids.size === 1) {
    const id = Array.from(selection.ids)[0]!;
    const kind =
      scope !== undefined && scope.id === id ? scope.kind : myGroups.find((g) => g.id === id)?.kind;
    if (kind === 'network') return I.network;
    if (kind === 'space') return I.chat;
    if (kind === 'circle') return I.circles;
  }
  return I.users;
}

function postAudience(
  selection: Extract<Selection, { kind: 'community' | 'spaces' }>,
  myGroups: ReadonlyArray<ComposerGroup>,
  scope: PostComposerScope | undefined,
): PostAudience {
  if (selection.kind === 'community') return 'community';
  if (selection.ids.size > 1) return 'multi';
  const id = Array.from(selection.ids)[0]!;
  const kind =
    scope !== undefined && scope.id === id ? scope.kind : myGroups.find((g) => g.id === id)?.kind;
  // Selected ids come from `scope` or the `myGroups` picker, so the lookup
  // only misses if the groups list changed mid-session; 'group' is the
  // generic bucket for that case.
  return kind !== undefined ? groupKindToType(kind) : 'group';
}

function looksLikeFirstPartyUrl(value: string): boolean {
  // Cheap pre-check before round-tripping to the resolver. The resolver does
  // the strict parse + host-equality check; this just filters out the
  // obviously-not-a-URL cases (e.g. typed prose).
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

function extractCandidateUrl(text: string): string | null {
  const trimmed = text.trim();
  if (looksLikeFirstPartyUrl(trimmed)) return trimmed;
  return null;
}

// Fallback card for a pasted link that can't be unfurled into a rich preview
// (unreachable, non-HTML, or — commonly — a 403 bot-wall like ScienceDirect).
// Carries only the URL; LinkPreviewCard renders the domain + a link glyph, matching
// the bare link chip iMessage/Slack show for un-unfurlable links. This is what
// keeps a pasted link from flashing-then-vanishing when there's no OG data.
function minimalLinkPreview(url: string): LinkPreviewInput {
  return {
    url,
    title: null,
    description: null,
    imageStorageKey: null,
    siteName: null,
  };
}

function draftKeyFor(viewerId: string, scope: PostComposerScope | undefined): string {
  const scopeId = scope !== undefined ? scope.id : 'global';
  return `composer-draft-v1-${viewerId}-${scopeId}`;
}

function newClientId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

function classifyMimeKind(mime: string): 'image' | 'pdf' | 'doc' | null {
  if (mime === 'application/pdf') return 'pdf';
  if (mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
    return 'doc';
  }
  if (mime.startsWith('image/')) return 'image';
  return null;
}

function toPostMediaInput(m: StagedMedia): PostMediaInput {
  const out: PostMediaInput = {
    key: m.storageKey!,
    contentType: m.mimeType,
    sizeBytes: m.sizeBytes,
    decorative: m.decorative,
  };
  if (m.width !== undefined) out.width = m.width;
  if (m.height !== undefined) out.height = m.height;
  if (m.altText !== null && m.altText.length > 0) out.altText = m.altText;
  return out;
}

// Crop is offered when the tile's bytes are reachable as a crop source —
// either the composer still holds the picked File, or the tile is committed
// (edit mode's previously-posted media, reload-restored drafts) and its
// image can be fetched back into a File from the store URL — and the upload
// has settled successfully: the crop replaces the uploaded object, so an
// in-flight or failed upload has nothing coherent to replace.
function canCropTile(m: StagedMedia): boolean {
  if (m.uploading === true || (m.uploadError !== null && m.uploadError !== undefined)) {
    return false;
  }
  if (m.file !== null) return CROPPABLE_IMAGE_TYPES.includes(m.file.type);
  return (
    CROPPABLE_IMAGE_TYPES.includes(m.mimeType) && m.url.length > 0 && !m.url.startsWith('blob:')
  );
}

async function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  if (!file.type.startsWith('image/')) return null;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}

export const PostComposerSheet = ({
  viewer,
  onClose,
  scope,
  myGroups = [],
  composerHandle,
  autoFocusOnMount = false,
  initialAttached,
  draftKeyOverride,
  chrome = 'card',
  toolbarExtras,
  mode = 'create',
  editPostId,
  initialBody,
  initialBodyFormat,
  initialMentions,
  initialMedia,
  onSaved,
  onPosted,
}: Props) => {
  const t = useTranslations('Feed');
  const isEdit = mode === 'edit';
  const firstName = viewer.name.split(' ')[0] ?? viewer.name;
  // Seeded in edit mode (the post's current body) AND in create mode (a
  // share modal's pre-quoted text). Like `initialAttached`, a create-mode
  // seed only holds until the draft-load effect finds a persisted draft for
  // this draft key — the user's saved words win over a re-seed.
  const [body, setBody] = useState(() =>
    initialBodyFormat === 'v1' ? normalizeLegacyBody(initialBody ?? '') : (initialBody ?? ''),
  );
  const [error, setError] = useState<string | null>(null);

  const createPostMutation = useCreateFeedPost();
  const updatePostMutation = useUpdateFeedPost();
  const presignMutation = usePresignFeedPostMedia();
  const finalizeMutation = useFinalizeFeedPostMedia();
  const resolveUrlMutation = useResolveFeedUrl();
  const resolveAttachmentMutation = useResolveFeedAttachment();
  const resolveLinkPreviewMutation = useResolveLinkPreview();
  const isPending = isEdit ? updatePostMutation.isPending : createPostMutation.isPending;
  // Rollout gate: poll authoring is hidden unless the `feed.polls` flag is on
  // for this viewer (GET /posts/capabilities). Default-deny while the query
  // resolves. Create-only — the edit composer never shows the button, so skip
  // the fetch there. The create route re-checks the flag; this is cosmetic.
  const pollsEnabled = usePollComposerEnabled({ enabled: !isEdit });
  // Stable id derived from viewer + scope. `useId` is hostile here: any tree
  // shape change above the composer (e.g. conditional banner, role-gated
  // section, modal portal mounting) shifts React's id counter and produces a
  // hydration mismatch on the label/textarea pair. The id only needs to be
  // unique among labels on this page, which `viewer.id + scope.id` already is.
  const editorId = `composer-body-${viewer.id}-${scope !== undefined ? scope.id : 'global'}`;
  // Char-limit guard shared by the editor's onChange (formatting/shortcuts) and
  // its onInput (typing). Returns false when the paste/keystroke would exceed
  // the cap so the caller can skip its follow-up work (e.g. mention refresh).
  const applyBody = useCallback(
    (md: string): boolean => {
      if (md.length > 10_000) {
        setError(t('composer.errors.tooLong'));
        return false;
      }
      setError(null);
      setBody(md);
      return true;
    },
    [t],
  );
  // handle → display name for every mention the body can contain: seeded from
  // the edited post's resolved mentions, grown per picker selection, persisted
  // with the draft. Ref-stable Map — hydrate reads the live contents.
  const mentionNamesRef = useRef<Map<string, string>>(
    new Map(initialMentions?.map((m) => [m.handle, m.name])),
  );
  const editorHandleRef = useRef<RichTextEditorHandle | null>(null);
  const [editorInstance, setEditorInstance] = useState<Editor | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Attachment state — up to MAX_ATTACHMENTS entities, in display order.
  // Each click on the Attach button appends; the button disables at the cap.
  // The .Editable card carries its own remove affordance per-position.
  // `initialAttached` seeds the list on first mount; the draft-load effect
  // below overwrites it when a persisted draft exists for this draft key.
  const [attached, setAttached] = useState<ReadonlyArray<AttachedEntityView>>(
    () => initialAttached ?? [],
  );
  const [attachError, setAttachError] = useState<string | null>(null);

  // Link previews — up to MAX_LINK_PREVIEWS Open Graph cards unfurled from
  // external URLs pasted into the body. Independent of `attached` (which holds
  // first-party entity attachments). Create mode only; edit is body-only.
  const [linkPreviews, setLinkPreviews] = useState<LinkPreviewInput[]>([]);
  // URLs currently being unfurled — render a loading placeholder card for each
  // so a slow (multi-second) resolve reads as "working", not "nothing happened".
  const [pendingPreviewUrls, setPendingPreviewUrls] = useState<string[]>([]);

  // Staged poll — null unless the member opened the poll builder. Create mode
  // only (polls are not editable). Mutually exclusive with media / attachments
  // / link previews, matching the server's create guard.
  const [poll, setPoll] = useState<PollDraft | null>(null);

  // Upload state — staged media list with per-tile progress/error. Edit mode
  // seeds it from the post's published media; those tiles carry their
  // post_media row id and no storage key (the server keeps their bytes).
  const [media, setMedia] = useState<StagedMedia[]>(() =>
    isEdit && initialMedia !== undefined
      ? initialMedia.map((v) => ({
          ...v,
          storageKey: null,
          file: null,
          sizeBytes: v.sizeBytes,
          // Rule #3 exception: PostMediaView.contentType is a generic string
          // by design (read/write model divergence — see zod-schemas.ts) but
          // the server never writes a value outside PostMediaContentType.
          mimeType: v.contentType as PostMediaContentType,
          existingId: v.id,
          uploading: false,
          uploadPct: null,
          uploadError: null,
        }))
      : [],
  );
  const semaphoreRef = useRef(new UploadSemaphore(MAX_INFLIGHT_UPLOADS));
  const blobUrlsRef = useRef<Set<string>>(new Set());
  // Revoke any outstanding object URLs when the composer unmounts.
  useEffect(() => {
    const refs = blobUrlsRef.current;
    return () => {
      for (const u of refs) URL.revokeObjectURL(u);
      refs.clear();
    };
  }, []);

  // Composer is collapsible everywhere: home + every scoped feed tab. Only
  // modal mode (onClose passed) starts expanded — the dialog has no pill
  // resting state to collapse to. Scoped tabs (circle / network / group feed)
  // present a pill prompt with a scope-aware placeholder.
  const startsExpanded = onClose !== undefined;
  const [expanded, setExpanded] = useState(startsExpanded);

  // Post-success celebration state — only used in flush (Dialog-hosted)
  // chrome. In inline `card` chrome the new post is visible in the feed
  // below, so the composer closes silently as before.
  const [posted, setPosted] = useState<{
    postId: string;
    audienceLabel: string;
  } | null>(null);
  const navigate = useNavigate();

  const pendingFocusRef = useRef(false);
  const pendingInsertRef = useRef<string | null>(null);

  // Transient nudge shown after a rail-prompt prefill: the inserted text is
  // ready, but the member still has to pick the audience pill. Shown once
  // per device (localStorage), dismissed by the next click anywhere.
  const [audienceHint, setAudienceHint] = useState(false);

  useEffect(() => {
    if (!audienceHint) return;
    const dismiss = () => setAudienceHint(false);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dismiss();
    };
    // Defer past the click that opened the composer, then any click — a
    // pill, the editor, anywhere — or ESC dismisses (the tooltip is
    // owner-controlled, so Radix's own ESC handling doesn't apply).
    const id = setTimeout(() => {
      window.addEventListener('click', dismiss, { capture: true, once: true });
      window.addEventListener('keydown', onKeyDown, { capture: true });
    }, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener('click', dismiss, { capture: true });
      window.removeEventListener('keydown', onKeyDown, { capture: true });
    };
  }, [audienceHint]);

  useImperativeHandle(
    composerHandle,
    () => ({
      focus: () => {
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (expanded) {
          editorHandleRef.current?.focus();
        } else {
          // Defer the editor focus until the expanded form mounts; the
          // editor's autoFocus then takes the cursor.
          pendingFocusRef.current = true;
          setExpanded(true);
        }
      },
      prefill: (text: string) => {
        rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try {
          if (window.localStorage.getItem(AUDIENCE_HINT_SEEN_KEY) === null) {
            window.localStorage.setItem(AUDIENCE_HINT_SEEN_KEY, '1');
            setAudienceHint(true);
          }
        } catch {
          // Storage unavailable (private mode) — skip the nudge silently.
        }
        if (expanded) {
          editorHandleRef.current?.focus();
          editorHandleRef.current?.insertText(text);
        } else {
          pendingFocusRef.current = true;
          pendingInsertRef.current = text;
          setExpanded(true);
        }
      },
    }),
    [expanded],
  );

  useEffect(() => {
    if (!autoFocusOnMount) return;
    rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (startsExpanded) {
      editorHandleRef.current?.focus();
    } else {
      pendingFocusRef.current = true;
      setExpanded(true);
    }
    // Mount-only by design: the prop answers "did the viewer arrive via a
    // compose deep link", which cannot become true after mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (expanded && pendingFocusRef.current) {
      pendingFocusRef.current = false;
      editorHandleRef.current?.focus();
      const pendingInsert = pendingInsertRef.current;
      if (pendingInsert !== null) {
        pendingInsertRef.current = null;
        editorHandleRef.current?.insertText(pendingInsert);
      }
    }
  }, [expanded]);
  const [selection, setSelection] = useState<Selection>(() =>
    scope !== undefined ? { kind: 'spaces', ids: new Set([scope.id]) } : { kind: 'community' },
  );

  // ---- Draft persistence ----------------------------------------------
  const draftKey = useMemo(
    () => draftKeyOverride ?? draftKeyFor(viewer.id, scope),
    [draftKeyOverride, viewer.id, scope],
  );
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (isEdit) return;
    if (draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(draftKey);
      if (raw === null) return;
      // `raw` is untrusted (written by an older build, or hand-edited) —
      // parse it into a validated shape before trusting any field. A
      // non-JSON `raw` throws here and falls to the outer catch, same as an
      // unrecognized draft shape falling through to the null check below.
      const parsed: StoredComposerDraft | null = parseStoredComposerDraft(JSON.parse(raw));
      if (parsed === null) return;
      if (parsed.mentionNames !== undefined) {
        for (const [handle, name] of Object.entries(parsed.mentionNames)) {
          mentionNamesRef.current.set(handle, name);
        }
      }
      setBody(parsed.v === 3 ? normalizeLegacyBody(parsed.body) : parsed.body);
      if (parsed.audienceSpaceIds !== undefined && parsed.audienceSpaceIds.length > 0) {
        setSelection({ kind: 'spaces', ids: new Set(parsed.audienceSpaceIds) });
      }
      // A poll needs no re-resolution — it holds only labels + settings.
      // `parseStoredComposerDraft` already ran it through
      // `parseStoredPollDraft`, so `null` here means either no poll was
      // saved or it couldn't be recovered — both are a no-op.
      if (parsed.poll !== null) {
        setPoll(parsed.poll);
      }
      // Link previews carry re-hosted image keys + fully-resolved OG metadata,
      // so — unlike entity attachments — they need no re-resolution on load.
      if (parsed.linkPreviews.length > 0) {
        setLinkPreviews(parsed.linkPreviews.slice(0, MAX_LINK_PREVIEWS));
      }
      // Re-resolve each attachment on load — visibility may have changed
      // independently per attachment. Resolved views land in the saved
      // (display) order; any individually-revoked entry is silently dropped.
      // A draft with zero attachments explicitly clears any `initialAttached`
      // seed — the user's saved decision (e.g. "I removed the entity") wins.
      if (parsed.attachments.length === 0) {
        setAttached([]);
      }
      if (parsed.attachments.length > 0) {
        // Filter legacy circle/network kinds out of saved drafts before
        // resolving — the server no longer accepts them, so any such entry
        // would just 400. Silent drop matches the "not visible anymore" UX.
        const resolvable = parsed.attachments.flatMap((a) =>
          isAttachableInputKind(a.kind) ? [{ kind: a.kind, id: a.id }] : [],
        );
        void Promise.all(
          resolvable.map((a) =>
            resolveAttachmentMutation
              .mutateAsync(a)
              .then((r) => ({ ok: true as const, attachment: r.attachment }))
              .catch(() => ({ ok: false as const, attachment: null })),
          ),
        ).then((results) => {
          const hydrated: AttachedEntityView[] = [];
          let anyHidden = false;
          for (const res of results) {
            if (res.ok && res.attachment !== null) {
              hydrated.push(res.attachment);
            } else if (res.ok) {
              anyHidden = true;
            }
          }
          if (hydrated.length > 0) setAttached(hydrated);
          if (anyHidden) {
            setAttachError(t('composer.errors.attachmentNotVisible'));
          }
        });
      }
      // Hydrate media metadata (URLs only — no blob refs across reloads).
      if (parsed.media.length > 0) {
        setMedia(
          parsed.media.map((m, idx) => {
            const kind = classifyMimeKind(m.contentType) ?? 'image';
            const draftUrl = mediaUrlFromKey(m.key) ?? '';
            const view: StagedMedia = {
              id: `draft-${idx}-${m.key}`,
              kind,
              url: draftUrl,
              // Composer tiles preview the full render; renditions only
              // matter once the post is projected back to readers.
              cardUrl: null,
              contentType: m.contentType,
              altText: m.altText,
              // Older drafts predate decorative-by-default uploads; without
              // alt text the server CHECK rejects a non-decorative image.
              decorative: m.altText.trim().length > 0 ? m.decorative : true,
              storageKey: m.key,
              file: null,
              sizeBytes: m.sizeBytes,
              mimeType: m.contentType,
              existingId: null,
              uploading: false,
              uploadPct: null,
              uploadError: null,
              thumbUrl: null,
              pageCount: null,
              ...(m.width !== undefined ? { width: m.width } : {}),
              ...(m.height !== undefined ? { height: m.height } : {}),
            };
            return view;
          }),
        );
      }
    } catch {
      // Ignore corrupt drafts.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  useEffect(() => {
    if (isEdit) return;
    if (typeof window === 'undefined') return;
    if (!draftLoadedRef.current) return;
    const draft: StoredComposerDraft = {
      v: DRAFT_VERSION,
      body,
      mentionNames: Object.fromEntries(mentionNamesRef.current),
      attachments: attached.map((a) => ({ kind: a.kind, id: a.id })),
      media: media
        .filter((m) => m.storageKey !== null && m.uploading !== true)
        .map((m) => ({
          key: m.storageKey!,
          contentType: m.mimeType,
          sizeBytes: m.sizeBytes,
          ...(m.width !== undefined ? { width: m.width } : {}),
          ...(m.height !== undefined ? { height: m.height } : {}),
          altText: m.altText ?? '',
          decorative: m.decorative,
        })),
      linkPreviews,
      poll,
      ...(selection.kind === 'spaces' ? { audienceSpaceIds: [...selection.ids] } : {}),
    };
    if (
      draft.body.length === 0 &&
      draft.attachments.length === 0 &&
      draft.media.length === 0 &&
      draft.linkPreviews.length === 0 &&
      draft.poll === null
    ) {
      window.localStorage.removeItem(draftKey);
    } else {
      window.localStorage.setItem(draftKey, JSON.stringify(draft));
    }
  }, [isEdit, body, attached, media, linkPreviews, poll, selection, draftKey]);

  const isCommunity = selection.kind === 'community';
  const isSpaceChecked = (id: string) => selection.kind === 'spaces' && selection.ids.has(id);

  const label = audienceLabel(selection, myGroups, scope, t);
  const labelIcon = audienceIcon(selection, myGroups, scope);
  const placeholderTarget =
    scope === undefined
      ? t('composer.targetCommunity')
      : (scope.name ??
        (scope.kind === 'circle'
          ? t('composer.targetCircle')
          : scope.kind === 'network'
            ? t('composer.targetNetwork')
            : t('composer.targetGroup')));
  const placeholder = t('composer.placeholder', {
    target: placeholderTarget,
    name: firstName,
  });

  const toggleCommunity = () => {
    setSelection((s) => (s.kind === 'community' ? { kind: 'none' } : { kind: 'community' }));
  };

  const toggleSpace = (id: string) => {
    setSelection((s) => {
      if (s.kind !== 'spaces') return { kind: 'spaces', ids: new Set([id]) };
      const next = new Set(s.ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next.size === 0 ? { kind: 'none' } : { kind: 'spaces', ids: next };
    });
  };

  const reset = () => {
    setBody('');
    editorHandleRef.current?.clear();
    setError(null);
    setAttached([]);
    setAttachError(null);
    setLinkPreviews([]);
    setPendingPreviewUrls([]);
    setPoll(null);
    setMedia((prev) => {
      // Revoke any blob: previews on reset.
      for (const m of prev) {
        if (m.url.startsWith('blob:')) URL.revokeObjectURL(m.url);
      }
      return [];
    });
    setExpanded(startsExpanded);
    if (scope === undefined) setSelection({ kind: 'community' });
    // Draft persistence is create-mode only — the load/save effects above both
    // bail when `isEdit`, so edit mode never writes this key. But the edit
    // composer shares the create composer's per-viewer/per-scope key, so a
    // blanket removeItem here on Cancel would wipe an unrelated in-progress
    // create draft (e.g. a half-written home-feed post). Only clear it when
    // this composer actually owns the draft.
    if (!isEdit && typeof window !== 'undefined') {
      window.localStorage.removeItem(draftKey);
    }
  };

  const cancel = () => {
    reset();
    onClose?.();
  };

  // ---- Paste handler --------------------------------------------------
  // Non-URL pastes fall through to TipTap's native paste, which maps rich
  // clipboard content onto the dialect's marks. A candidate URL is consumed
  // (return true) so it can become an attachment card or link preview; the
  // async continuation inserts `fallbackText` through the editor handle on
  // the fallback paths — null when the editor already applied the URL as a
  // link on the selection, in which case nothing may be inserted.
  const resolvePastedUrl = useCallback(
    async (fallbackText: string | null, candidate: string, canAttachMore: boolean) => {
      // `resolved.attachment.kind` is the narrowed `AttachableInputKind` set
      // (server-side `ResolveUrlResponse` schema). Let TS infer from the
      // mutation's return type rather than re-stating the shape with the
      // wider `AttachedEntityKind`, which would force casts downstream.
      let resolved: Awaited<ReturnType<typeof resolveUrlMutation.mutateAsync>>;
      try {
        resolved = await resolveUrlMutation.mutateAsync({ url: candidate });
      } catch (err) {
        setAttachError(feedErrorMessage(err, t, 'composer.errors.resolveUrl'));
        return;
      }
      // External URL (no first-party entity): keep the URL in the body as text
      // — like iMessage / LinkedIn — and, outside edit mode, try to unfurl a
      // rich link-preview card for it. The preview is best-effort: any failure
      // or empty result leaves the URL text untouched.
      if (resolved.attachment === null) {
        if (fallbackText !== null) {
          editorHandleRef.current?.insertText(fallbackText);
        }
        if (isEdit) return;
        // Previews are built only for https URLs — both the resolve endpoint
        // and the create-post contract require https, so an http link stays as
        // plain text rather than becoming a card that would fail submission.
        if (!candidate.startsWith('https://')) return;
        if (linkPreviews.length + pendingPreviewUrls.length >= MAX_LINK_PREVIEWS) return;
        if (linkPreviews.some((p) => p.url === candidate) || pendingPreviewUrls.includes(candidate))
          return;
        // Show a loading placeholder immediately; the unfurl can take a few
        // seconds (fetch + image re-host), and silence reads as failure.
        setPendingPreviewUrls((prev) => [...prev, candidate]);
        // A pasted link ALWAYS resolves to a card: a rich Open Graph preview
        // when the site is fetchable, otherwise a minimal card (domain + link).
        // Many sites (bot-walled journals like ScienceDirect, some social
        // networks) return 403 to every server-side fetch, so the null/error
        // paths are common — they must degrade to a bare card, never vanish.
        let preview: LinkPreviewInput;
        try {
          const unfurled = await resolveLinkPreviewMutation.mutateAsync({
            url: candidate,
          });
          preview = unfurled.preview ?? minimalLinkPreview(candidate);
        } catch {
          preview = minimalLinkPreview(candidate);
        }
        setPendingPreviewUrls((prev) => prev.filter((u) => u !== candidate));
        setLinkPreviews((prev) => {
          if (prev.length >= MAX_LINK_PREVIEWS) return prev;
          if (prev.some((p) => p.url === preview.url)) return prev;
          return [...prev, preview];
        });
        return;
      }
      // First-party entity URL → attachment card (unchanged behavior). At the
      // attachment cap, fall back to inserting the URL as plain text.
      const match = resolved.attachment;
      if (!canAttachMore) {
        if (fallbackText !== null) {
          editorHandleRef.current?.insertText(fallbackText);
        }
        return;
      }
      // Skip if already attached (paste of a URL we already have).
      if (attached.some((a) => a.kind === match.kind && a.id === match.id)) {
        return;
      }
      try {
        const hydrated = await resolveAttachmentMutation.mutateAsync(match);
        if (hydrated.attachment !== null) {
          const view = hydrated.attachment;
          setAttached((prev) => (prev.length >= MAX_ATTACHMENTS ? prev : [...prev, view]));
          setAttachError(null);
        } else if (fallbackText !== null) {
          editorHandleRef.current?.insertText(fallbackText);
        }
      } catch {
        if (fallbackText !== null) {
          editorHandleRef.current?.insertText(fallbackText);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attached, linkPreviews, pendingPreviewUrls, isEdit],
  );

  const handlePasteText = useCallback(
    (text: string, ctx: { readonly linkApplied: boolean }): boolean => {
      // Edit mode never submits attachments or link previews, so a pasted URL
      // stays plain text there; in create mode it can become an attachment
      // (first-party entity) or an unfurled link-preview card. When the
      // editor already applied the URL as a link on the selection, previews
      // and attachments still resolve, but the text-insertion fallbacks are
      // suppressed — the link is already in the body.
      // A staged poll precludes attachments and link previews (server create
      // guard), so a pasted URL stays plain text while the poll builder is open.
      const canAttachMore = !isEdit && poll === null && attached.length < MAX_ATTACHMENTS;
      const canPreviewMore =
        !isEdit &&
        poll === null &&
        linkPreviews.length + pendingPreviewUrls.length < MAX_LINK_PREVIEWS;
      const candidate = canAttachMore || canPreviewMore ? extractCandidateUrl(text) : null;
      if (candidate === null) return false;
      void resolvePastedUrl(ctx.linkApplied ? null : text, candidate, canAttachMore);
      return true;
    },
    [attached, linkPreviews, pendingPreviewUrls, poll, isEdit, resolvePastedUrl],
  );

  // ---- Upload handler -------------------------------------------------
  const onUploadFiles = useCallback(
    async (files: File[]) => {
      // Filter to accepted types + size cap; bad files become a tile with
      // an inline error so the user sees what happened.
      const tiles: StagedMedia[] = files.map((file) => {
        const id = `local-${crypto.randomUUID()}`;
        const kind = classifyMimeKind(file.type) ?? 'image';
        const url = URL.createObjectURL(file);
        blobUrlsRef.current.add(url);
        const tile: StagedMedia = {
          id,
          kind,
          url,
          cardUrl: null,
          contentType: file.type,
          altText: '',
          // The composer offers no alt-text field, so uploads are stored as
          // decorative — the DB requires images to carry alt text or the
          // decorative flag. The API still accepts alt text (PostMediaInput /
          // UpdatePostMediaKeep) if a describe-image affordance returns.
          decorative: true,
          storageKey: null,
          file,
          sizeBytes: file.size,
          // Invariant #4 exception: StagedMedia is a flag bag, not a
          // discriminated union, so a rejected tile (uploadError set below,
          // never uploaded or submitted) still needs a mimeType. The
          // placeholder is never read; modelling rejected tiles as their own
          // state removes it.
          mimeType: isPostMediaContentType(file.type) ? file.type : POST_MEDIA_CONTENT_TYPES[0],
          existingId: null,
          uploading: true,
          uploadPct: 0,
          uploadError: null,
          thumbUrl: null,
          pageCount: null,
        };
        if (!ACCEPTED_UPLOAD_TYPES.includes(file.type)) {
          tile.uploading = false;
          tile.uploadError = t('composer.errors.unsupportedType');
        } else if (file.size > MAX_UPLOAD_BYTES) {
          tile.uploading = false;
          tile.uploadError = t('composer.errors.tooLarge');
        }
        return tile;
      });
      setMedia((prev) => [...prev, ...tiles]);
      // Run uploads through the semaphore.
      for (const tile of tiles) {
        if (tile.uploadError !== null) continue;
        void uploadOne(tile);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const uploadOne = useCallback(async (tile: StagedMedia): Promise<void> => {
    if (tile.file === null) return;
    await semaphoreRef.current.run(async () => {
      const dims = await readImageDimensions(tile.file!);
      let presign: { uploadUrl: string; fields: Record<string, string>; key: string };
      try {
        presign = await presignMutation.mutateAsync({
          contentType: tile.mimeType,
          sizeBytes: tile.sizeBytes,
        });
      } catch (err) {
        setMedia((prev) =>
          prev.map((m) =>
            m.id === tile.id
              ? {
                  ...m,
                  uploading: false,
                  uploadError: feedErrorMessage(err, t, 'composer.errors.startUpload'),
                }
              : m,
          ),
        );
        return;
      }
      try {
        await uploadWithProgress({
          url: presign.uploadUrl,
          body: tile.file!,
          headers: presign.fields,
          onProgress: (pct) => {
            setMedia((prev) => prev.map((m) => (m.id === tile.id ? { ...m, uploadPct: pct } : m)));
          },
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : t('composer.errors.uploadFailed');
        setMedia((prev) =>
          prev.map((m) =>
            m.id === tile.id ? { ...m, uploading: false, uploadError: message } : m,
          ),
        );
        return;
      }
      // Industry-standard "process at upload time" — after the PUT lands,
      // call the finalize endpoint so the preview tile can render the
      // canonical PDF thumbnail / page count / re-encoded image size
      // before the user clicks Post. Server is the authority on these
      // values; `createPost` re-runs the same pipeline as defense in
      // depth (idempotent on the derived keys).
      let finalized: FinalizePostMediaResult;
      try {
        finalized = await finalizeMutation.mutateAsync({
          key: presign.key,
          contentType: tile.mimeType,
          sizeBytes: tile.sizeBytes,
        });
      } catch (err) {
        setMedia((prev) =>
          prev.map((m) =>
            m.id === tile.id
              ? {
                  ...m,
                  uploading: false,
                  uploadError: feedErrorMessage(err, t, 'composer.errors.processFile'),
                }
              : m,
          ),
        );
        return;
      }
      setMedia((prev) =>
        prev.map((m) =>
          m.id === tile.id
            ? {
                ...m,
                uploading: false,
                uploadPct: 100,
                uploadError: null,
                storageKey: presign.key,
                kind: finalized.kind,
                contentType: finalized.contentType,
                // Rule #3 exception: FinalizePostMediaResponse.contentType is
                // a generic string by design (read/write model divergence —
                // see zod-schemas.ts); the server only ever returns a value
                // in PostMediaContentType (e.g. HEIC normalized to JPEG).
                mimeType: finalized.contentType as PostMediaContentType,
                sizeBytes: finalized.sizeBytes,
                thumbUrl: finalized.thumbUrl,
                pageCount: finalized.pageCount,
                ...(finalized.width !== null && finalized.height !== null
                  ? { width: finalized.width, height: finalized.height }
                  : dims !== null
                    ? { width: dims.width, height: dims.height }
                    : {}),
              }
            : m,
        ),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onMediaRemove = (id: string) => {
    setMedia((prev) => {
      const removed = prev.find((m) => m.id === id);
      if (removed?.url.startsWith('blob:') === true) {
        URL.revokeObjectURL(removed.url);
        blobUrlsRef.current.delete(removed.url);
      }
      return prev.filter((m) => m.id !== id);
    });
  };
  const onMediaReorder = (fromId: string, toId: string) => {
    setMedia((prev) => {
      const fromIdx = prev.findIndex((m) => m.id === fromId);
      const toIdx = prev.findIndex((m) => m.id === toId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved!);
      return next;
    });
  };
  const onMediaRetry = (id: string) => {
    const tile = media.find((m) => m.id === id);
    if (tile === undefined || tile.file === null) return;
    setMedia((prev) =>
      prev.map((m) =>
        m.id === id ? { ...m, uploading: true, uploadError: null, uploadPct: 0 } : m,
      ),
    );
    void uploadOne(tile);
  };

  // ---- Crop -------------------------------------------------------------
  // `file` is the crop source: the retained picked File when the composer
  // still has one, otherwise the committed image fetched back from the store
  // (edit mode's previously-posted media, reload-restored drafts).
  const [cropState, setCropState] = useState<{
    id: string;
    file: File;
    aspect: number;
    options: Array<{ label: string; value: number }>;
  } | null>(null);
  const cropTile = cropState !== null ? media.find((m) => m.id === cropState.id) : undefined;

  const onMediaCrop = (id: string) => {
    const tile = media.find((m) => m.id === id);
    if (tile === undefined || !canCropTile(tile)) return;
    void (async () => {
      let source = tile.file;
      if (source === null) {
        try {
          source = await fetchImageAsFile(tile.url, 'post-image');
        } catch {
          setError(t('composer.errors.processFile'));
          return;
        }
      }
      const dims =
        tile.width !== undefined && tile.height !== undefined
          ? { width: tile.width, height: tile.height }
          : await readImageDimensions(source);
      if (dims === null || dims.width === 0 || dims.height === 0) return;
      const original = dims.width / dims.height;
      // Dedupe by value: an exactly-4:3 or exactly-square source would
      // otherwise produce two toggle items with the same value.
      const options = [
        { label: t('media.cropOriginal'), value: original },
        { label: t('media.cropGallery'), value: 4 / 3 },
        { label: t('media.cropSquare'), value: 1 },
      ].filter((opt, idx, all) => all.findIndex((o) => o.value === opt.value) === idx);
      setCropState({ id, file: source, aspect: original, options });
    })();
  };

  // Swaps the cropped file into the tile's slot and re-runs the standard
  // upload pipeline (presign → PUT → finalize) under the same tile id, so
  // position, progress, and error handling all behave like a fresh upload.
  // The previously-uploaded object is abandoned — the server sweeps
  // unreferenced uploads.
  const onCropConfirm = (id: string, cropped: File) => {
    setCropState(null);
    const tile = media.find((m) => m.id === id);
    if (tile === undefined) return;
    if (tile.url.startsWith('blob:')) {
      URL.revokeObjectURL(tile.url);
      blobUrlsRef.current.delete(tile.url);
    }
    const url = URL.createObjectURL(cropped);
    blobUrlsRef.current.add(url);
    // Drop the stale dimensions — finalize returns the cropped file's.
    const { width: _width, height: _height, ...rest } = tile;
    const next: StagedMedia = {
      ...rest,
      url,
      contentType: cropped.type,
      storageKey: null,
      file: cropped,
      sizeBytes: cropped.size,
      // The crop dialog only ever re-encodes to a CROPPABLE_IMAGE_TYPES
      // member (a subset of PostMediaContentType); fall back to the tile's
      // pre-crop mimeType in the unreachable case it doesn't.
      mimeType: isPostMediaContentType(cropped.type) ? cropped.type : tile.mimeType,
      // A cropped previously-posted image is a new object, not the published
      // row — dropping the row id makes the edit payload send it as a fresh
      // upload (by storage key) instead of re-referencing the old media.
      existingId: null,
      uploading: true,
      uploadPct: 0,
      uploadError: null,
      thumbUrl: null,
    };
    setMedia((prev) => prev.map((m) => (m.id === id ? next : m)));
    void uploadOne(next);
  };

  // ---- Submit ---------------------------------------------------------
  const trimmedBody = body.trim();
  const hasUploadingMedia = media.some((m) => m.uploading === true);
  const committedMedia = media.filter(
    (m) => (m.storageKey !== null || m.existingId !== null) && m.uploadError === null,
  );
  // A poll needs the post body as its question plus 2–4 valid option labels
  // (the same rule the server re-runs on create). No poll → no extra gate.
  const pollValid =
    poll === null || (trimmedBody.length > 0 && validatePollOptions(poll.options).ok);
  const canSubmit = isEdit
    ? !isPending &&
      !hasUploadingMedia &&
      // `attached` counts here because attachments aren't editable in edit
      // mode — a post that keeps its attached entities may clear body+media.
      (trimmedBody.length > 0 || committedMedia.length > 0 || attached.length > 0)
    : !isPending &&
      !hasUploadingMedia &&
      // A pasted URL still unfurling isn't in `linkPreviews` yet; publishing
      // now would drop its card (same hazard as an in-flight media upload).
      pendingPreviewUrls.length === 0 &&
      selection.kind !== 'none' &&
      (trimmedBody.length > 0 ||
        attached.length > 0 ||
        committedMedia.length > 0 ||
        linkPreviews.length > 0) &&
      pollValid;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (isEdit) {
      if (trimmedBody.length === 0 && committedMedia.length === 0 && attached.length === 0) {
        setError(t('composer.errors.writeOrAttach'));
        return;
      }
      if (hasUploadingMedia) {
        setError(t('composer.errors.waitUploads'));
        return;
      }
      if (trimmedBody.length > 10_000) {
        setError(t('composer.errors.tooLong'));
        return;
      }
      // Declarative full-state list: existing tiles by post_media row id
      // (altText null clears it), new uploads by storage key. Array order is
      // the display order.
      const editMediaPayload: UpdateFeedPostMediaItem[] = committedMedia.map((m) =>
        m.existingId !== null
          ? {
              id: m.existingId,
              altText: m.altText !== null && m.altText.trim().length > 0 ? m.altText.trim() : null,
              decorative: m.decorative,
            }
          : toPostMediaInput(m),
      );
      void (async () => {
        try {
          const updated = await updatePostMutation.mutateAsync({
            postId: editPostId!,
            body: trimmedBody,
            bodyFormat: EDITOR_BODY_FORMAT,
            media: editMediaPayload,
          });
          onSaved?.({
            body: updated.body,
            bodyFormat: updated.bodyFormat,
            mentions: updated.mentions,
            media: updated.media,
          });
          onClose?.();
        } catch (err) {
          setError(feedErrorMessage(err, t, 'composer.errors.saveFailed'));
        }
      })();
      return;
    }
    if (selection.kind === 'none') {
      setError(t('composer.errors.pickWhere'));
      return;
    }
    if (
      trimmedBody.length === 0 &&
      attached.length === 0 &&
      committedMedia.length === 0 &&
      linkPreviews.length === 0
    ) {
      setError(t('composer.errors.writeOrAttach'));
      return;
    }
    if (hasUploadingMedia) {
      setError(t('composer.errors.waitUploads'));
      return;
    }
    if (pendingPreviewUrls.length > 0) {
      setError(t('composer.errors.waitPreview'));
      return;
    }
    if (trimmedBody.length > 10_000) {
      setError(t('composer.errors.tooLong'));
      return;
    }
    const baseClientId = newClientId('composer');
    const postScope: components['schemas']['PostScopeInput'] =
      selection.kind === 'community'
        ? { isGlobal: true }
        : { isGlobal: false, groupIds: Array.from(selection.ids) };
    const mediaPayload: PostMediaInput[] = committedMedia.map(toPostMediaInput);
    void (async () => {
      try {
        const created = await createPostMutation.mutateAsync({
          body: trimmedBody,
          bodyFormat: EDITOR_BODY_FORMAT,
          clientPostId: baseClientId,
          scope: postScope,
          // Filter at the write boundary — `attached` state is typed against
          // the wider `AttachedEntityView` (read side) so legacy Circle/
          // Network drafts coexist; the server input no longer accepts them.
          attachedEntities: attached.flatMap((a) =>
            isAttachableInputKind(a.kind) ? [{ kind: a.kind, id: a.id }] : [],
          ),
          media: mediaPayload,
          // Submitted verbatim — the resolve result already carries the
          // re-hosted `imageStorageKey`; no client-side conversion.
          linkPreviews,
          // Trim option labels at the boundary; the server mints ids + closes-at
          // and re-validates count/uniqueness/length before writing.
          poll:
            poll !== null
              ? {
                  options: poll.options.map((o) => o.trim()),
                  durationDays: poll.durationDays,
                  visibility: poll.visibility,
                }
              : undefined,
        });
        onPosted?.({
          postId: created.id,
          audience: postAudience(selection, myGroups, scope),
          post: created,
        });
        if (chrome === 'flush') {
          const audience =
            audienceLabel(selection, myGroups, scope, t) ?? t('composer.audienceFallback');
          setPosted({ postId: created.id, audienceLabel: audience });
          reset();
        } else {
          reset();
          onClose?.();
        }
      } catch (err) {
        setError(feedErrorMessage(err, t, 'composer.errors.publishFailed'));
      }
    })();
  };

  if (chrome === 'flush' && posted !== null) {
    const goToPost = () => {
      onClose?.();
      void navigate({
        to: '/posts/$id',
        params: { id: posted.postId },
      });
    };
    return (
      <Stack
        gap="lg"
        align="center"
        px="xl"
        py="2xl"
        style={{
          textAlign: 'center',
          animation: 'fade-in 220ms ease-out both',
        }}
      >
        <div
          aria-hidden
          style={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            background: 'var(--success)',
            color: 'var(--success-foreground)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'scale-in 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
          }}
        >
          <Icon size={36}>{I.check}</Icon>
        </div>
        <Stack gap="xs" align="center">
          <Heading level={3}>{t('composer.successTitle')}</Heading>
          <Body color="mute">{t('composer.successBody', { audience: posted.audienceLabel })}</Body>
        </Stack>
        <Row gap="md" justify="center">
          <Button variant="ghost" size="md" onClick={() => onClose?.()}>
            {t('composer.done')}
          </Button>
          <Button variant="primary" size="md" onClick={goToPost}>
            {t('composer.viewPost')}
          </Button>
        </Row>
      </Stack>
    );
  }

  if (!expanded) {
    return (
      <div ref={rootRef} style={{ scrollMarginTop: 'var(--space-xl)' }}>
        <button
          type="button"
          onClick={() => {
            pendingFocusRef.current = true;
            setExpanded(true);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            // The avatar sits INSIDE the capsule (matching the expanded
            // composer); the bar's height grows from the avatar + the small
            // vertical padding rather than a fixed control height.
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <button> capsule prompt; no Box prop
            gap: 'var(--space-sm)',
            // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- raw <button> capsule prompt; no Box prop
            padding: 'var(--space-md) var(--space-lg) var(--space-md) var(--space-md)',
            borderRadius: 'var(--radius-full)',
            // Capsule module surface: same treatment as boxed modules — white
            // surface + soft shadow, no border (never border + shadow).
            background: 'var(--surface-module-bg)',
            border: 'var(--surface-module-border)',
            boxShadow: 'var(--surface-module-shadow)',
            textAlign: 'start',
            cursor: 'pointer',
          }}
        >
          <Avatar src={viewer.photoUrl} name={viewer.name} size={36} />
          <span
            style={{
              flex: 1,
              minWidth: 0,
              color: 'var(--muted-foreground)',
              font: 'var(--type-body)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {placeholder}
          </span>
        </button>
      </div>
    );
  }

  const showAudiencePicker = !isEdit && scope === undefined;
  // chrome='flush' callers (Dialog hosts) provide their own surface + close
  // button; the inline Card wrapper and X button would double the chrome.
  const renderInlineClose = chrome === 'card';

  const formContent = (
    <form ref={formRef} onSubmit={handleSubmit}>
      <Stack
        gap="md"
        // Edit mode is hosted inside the PostCard's own content padding;
        // its own horizontal padding would double the inset and push the
        // body + toolbar right of the post's pills. Drop it in edit mode.
        px={isEdit ? 'none' : 'xl'}
        pt="lg"
        pb="md"
      >
        {/* Edit mode is hosted inside the PostCard, which already shows
                the author's avatar + name — so the composer's own identity
                header would duplicate it. Create mode keeps it. */}
        {isEdit ? null : (
          <Row gap="md" align="center" justify="space-between">
            <Row gap="md" align="center">
              <Avatar src={viewer.photoUrl} name={viewer.name} size={36} />
              <Stack gap="xs">
                <Label>{viewer.name}</Label>
                {!isEdit && !showAudiencePicker && label !== null ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- inline-flex <span> pill-shaped audience label; no Box prop
                      gap: 'var(--space-sm)',
                      font: 'var(--type-body-sm)',
                      // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- inline-flex <span> pill-shaped audience label; no Box prop
                      padding: 'var(--space-xs) var(--space-md)',
                      borderRadius: 'var(--radius-full)',
                      border: '1px solid var(--surface-neutral-border)',
                      background: 'var(--surface-neutral)',
                      color: 'var(--foreground)',
                      width: 'fit-content',
                    }}
                  >
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        display: 'inline-flex',
                        color: 'var(--muted-foreground)',
                      }}
                    >
                      {labelIcon}
                    </span>
                    <span>{t('composer.postingTo', { label })}</span>
                  </span>
                ) : null}
              </Stack>
            </Row>
            {renderInlineClose ? (
              <Button
                variant="ghost"
                size="sm"
                icon={I.x}
                aria-label={t('composer.closeAria')}
                onClick={cancel}
              />
            ) : null}
          </Row>
        )}
        <RichTextEditor
          value={body}
          onChange={applyBody}
          placeholder={placeholder}
          variant="bare"
          minHeight={24}
          disabled={isPending}
          autoFocus
          id={editorId}
          ariaLabel={t('composer.bodyLabel')}
          handle={editorHandleRef}
          onEditorReady={setEditorInstance}
          onSubmitShortcut={() => formRef.current?.requestSubmit()}
          onPasteText={handlePasteText}
          mentionNames={mentionNamesRef.current}
          {...(scope !== undefined ? { mentionAudienceGroupId: scope.id } : {})}
        />
        {isEdit ? null : attached.length > 0 ? (
          attached.map((entity, idx) => (
            <PostEntityCard.Editable
              key={`${entity.kind}:${entity.id}`}
              entity={entity}
              onRemove={() => setAttached((prev) => prev.filter((_, i) => i !== idx))}
              {...(idx === attached.length - 1 && attachError !== null
                ? { errorMessage: attachError }
                : {})}
            />
          ))
        ) : attachError !== null ? (
          <Body color="destructive">{attachError}</Body>
        ) : null}
        {!isEdit && linkPreviews.length > 0
          ? linkPreviews.map((preview, idx) => (
              <LinkPreviewCard.Editable
                key={preview.url}
                preview={preview}
                onRemove={() => setLinkPreviews((prev) => prev.filter((_, i) => i !== idx))}
              />
            ))
          : null}
        {!isEdit
          ? pendingPreviewUrls.map((url) => <LinkPreviewCard.Loading key={url} url={url} />)
          : null}
        {media.length > 0 ? (
          <PostMediaGallery.Editable
            items={media.map((m) => (canCropTile(m) ? { ...m, croppable: true } : m))}
            onRemove={onMediaRemove}
            onReorder={onMediaReorder}
            onRetry={onMediaRetry}
            onCrop={onMediaCrop}
          />
        ) : null}
        {!isEdit && poll !== null ? (
          <PollComposerBlock draft={poll} onChange={setPoll} onRemove={() => setPoll(null)} />
        ) : null}
        {error !== null ? <Body color="destructive">{error}</Body> : null}
        {hasUploadingMedia ? <Caption aria-live="polite">{t('media.uploading')}</Caption> : null}
      </Stack>

      {showAudiencePicker ? (
        <Stack
          gap="sm"
          px="xl"
          py="md"
          style={{
            borderTop: '1px solid var(--border)',
          }}
          onClickCapture={() => setAudienceHint(false)}
        >
          <Overline case="upper" style={{ color: 'var(--muted-foreground)' }}>
            {t('composer.whereToPost')}
          </Overline>
          {audienceHint ? (
            <span
              role="status"
              aria-live="polite"
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                overflow: 'hidden',
                clipPath: 'inset(50%)',
              }}
            >
              {t('composer.audienceHint')}
            </span>
          ) : null}
          <Tooltip
            content={t('composer.audienceHint')}
            side="top"
            sideOffset={6}
            open={audienceHint}
            tone="accent"
          >
            <div>
              <Row wrap gap="sm" rowGap="sm">
                <Pill size="sm" tone="burgundy" selected={isCommunity} onClick={toggleCommunity}>
              {t('composer.audienceCommunity')}
            </Pill>
            {myGroups.map((g) => (
              <Pill
                key={g.id}
                size="sm"
                tone={g.kind === 'network' ? 'navy' : g.kind === 'space' ? 'muted' : 'burgundy'}
                selected={isSpaceChecked(g.id)}
                onClick={() => toggleSpace(g.id)}
              >
                {g.name}
              </Pill>
            ))}
              </Row>
            </div>
          </Tooltip>
        </Stack>
      ) : null}

      <Row
        justify="space-between"
        align="center"
        gap="sm"
        px={isEdit ? 'none' : 'xl'}
        py="sm"
        style={{
          borderTop: '1px solid var(--border)',
        }}
      >
        <Row gap="xs" align="center">
          <RichTextToolbar editor={editorInstance} disabled={isPending} />
          {/* Entity attachments come from pasting an in-app link (the
                  resolve-url paste flow) or a share modal's initialAttached
                  seed — there is no picker affordance. */}
          <PostMediaUploadButton
            label={t('composer.uploadMedia')}
            accept={MEDIA_UPLOAD_TYPES.join(',')}
            onFiles={(files) => void onUploadFiles(files)}
            // A staged poll precludes media (server create guard).
            disabled={isPending || poll !== null}
            // Edit mode lacks the create composer's surrounding chrome, so
            // the icon-only trigger reads as a formatting control there —
            // spell it out.
            variant={isEdit ? 'inline' : 'icon'}
          />
          <PostMediaUploadButton
            label={t('composer.uploadDocument')}
            accept={DOCUMENT_UPLOAD_TYPES.join(',')}
            icon={<FileTextIcon size="100%" strokeWidth={1.75} />}
            onFiles={(files) => void onUploadFiles(files)}
            disabled={isPending || poll !== null}
            variant={isEdit ? 'inline' : 'icon'}
          />
          {/* Poll is create-only (polls are not editable) and one content lane:
              disabled once media / attachments / link previews are present, and
              the upload buttons above disable once a poll is staged. Hidden
              entirely unless the `feed.polls` flag is on for this viewer. */}
          {!isEdit && pollsEnabled ? (
            <Button
              variant="ghost"
              size="sm"
              icon={<BarChart2 size="100%" strokeWidth={1.75} />}
              aria-label={t('poll.add')}
              disabled={
                isPending ||
                media.length > 0 ||
                attached.length > 0 ||
                linkPreviews.length > 0
              }
              onClick={() => setPoll(newPollDraft())}
            />
          ) : null}
          {toolbarExtras}
        </Row>
        <Row gap="sm" align="center">
          <Button variant="ghost" size="md" onClick={cancel} disabled={isPending}>
            {t('composer.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={!canSubmit}
            loading={isPending}
          >
            {isEdit ? t('composer.saveChanges') : t('composer.submit')}
          </Button>
        </Row>
      </Row>
    </form>
  );

  const composerCard =
    chrome === 'flush' ? (
      formContent
    ) : (
      <Card pad="none" style={{ overflow: 'hidden' }}>
        {formContent}
      </Card>
    );

  // Guard on the live tile — it could have been removed out from under the
  // dialog. The crop source File travels in cropState: committed tiles
  // (edit mode, restored drafts) hold no File of their own.
  const cropDialog =
    cropState !== null && cropTile !== undefined ? (
      <ImageCropDialog
        file={cropState.file}
        aspect={cropState.aspect}
        aspectOptions={cropState.options}
        onConfirm={(cropped) => onCropConfirm(cropState.id, cropped)}
        onCancel={() => setCropState(null)}
      />
    ) : null;

  // Edit mode: body, formatting, and media (upload button + file drops).
  // Attachment picking stays create-only.
  if (isEdit) {
    return (
      <div ref={rootRef} style={{ scrollMarginTop: 'var(--space-xl)' }}>
        <DropZone
          onFiles={(files) => void onUploadFiles(files)}
          accept={ACCEPTED_UPLOAD_TYPES.join(',')}
        >
          {composerCard}
        </DropZone>
        {cropDialog}
      </div>
    );
  }

  return (
    <div ref={rootRef} style={{ scrollMarginTop: 'var(--space-xl)' }}>
      <DropZone
        onFiles={(files) => void onUploadFiles(files)}
        accept={ACCEPTED_UPLOAD_TYPES.join(',')}
        // A staged poll precludes media (server create guard); block file drops
        // too, matching the disabled upload buttons. Guarding here (not inside
        // `onUploadFiles`, whose deps:[] closure would read a stale `poll`).
        disabled={poll !== null}
      >
        {composerCard}
      </DropZone>
      {cropDialog}
    </div>
  );
};

// ── Serialization helpers ──────────────────────────────────────────────────
// markdown ⇄ HTML lives in ../editor/markdown — shared with RichTextArea.

// Inserts plain text into the editor at the range captured when the paste
// happened, then syncs the markdown body state. Used by the async paste
// fallbacks, where the live selection may have collapsed or moved while the
// URL resolver was in flight. Falls back to the end of the editor when the
// captured range is gone (e.g. its node was removed by an intervening edit).
